import log from 'app/home/general-components/Logging';
import { useCallback, useReducer } from 'react';

export const AudioFilesFetchStatus = {
    nothing: 0,
    fetching: 1,
    fetched: 2,
}

export default function useAudioController(sendEvent) {
    const reducer = useCallback((currentAudioFilesData, e) => {
        let newAudioFilesData = undefined;
        switch (e.event) {
            case "GatewayAudioFilesEvent":
                switch (e.body.msg.sub_event) {
                    case 'folder_list':
                        newAudioFilesData = { ...currentAudioFilesData };
                        if (e.body.msg.folders !== undefined) {
                            newAudioFilesData.folders = e.body.msg.folders;
                            for (let i = 0; i < newAudioFilesData.folders.length; i++) {
                                const folder = newAudioFilesData.folders[i];
                                folder["tracksFetchStatus"] = AudioFilesFetchStatus.nothing;
                                folder["tracks"] = undefined;
                            }
                            newAudioFilesData.foldersFetchStatus = AudioFilesFetchStatus.fetched;
                        }
                        return newAudioFilesData;
                    case 'album_list':
                        newAudioFilesData = { ...currentAudioFilesData };
                        if (e.body.msg.albums !== undefined) {
                            newAudioFilesData.albums = e.body.msg.albums;
                            for (let i = 0; i < newAudioFilesData.albums.length; i++) {
                                const album = newAudioFilesData.albums[i];
                                album["tracksFetchStatus"] = AudioFilesFetchStatus.nothing;
                                album["tracks"] = undefined;
                            }
                            newAudioFilesData.albumsFetchStatus = AudioFilesFetchStatus.fetched;
                        }
                        return newAudioFilesData;
                    case 'track_list':
                        newAudioFilesData = { ...currentAudioFilesData };
                        if (e.body.msg.list_type === 1) { // folders
                            for (let i = 0; i < newAudioFilesData.folders.length; i++) {
                                const folder = newAudioFilesData.folders[i];
                                if (folder["path"] === e.body.msg.list_path) {
                                    const folder2 = { ...folder }
                                    folder2["tracksFetchStatus"] = AudioFilesFetchStatus.fetched;
                                    folder2["tracks"] = e.body.msg.tracks;
                                    newAudioFilesData.folders[i] = folder2;
                                }
                            }
                        } else if (e.body.msg.list_type === 2) { // albums
                            for (let i = 0; i < newAudioFilesData.albums.length; i++) {
                                const album = newAudioFilesData.albums[i];
                                if (album["name"] === e.body.msg.list_name) {
                                    const album2 = { ...album }
                                    album2["tracksFetchStatus"] = AudioFilesFetchStatus.fetched;
                                    album2["tracks"] = e.body.msg.tracks;
                                    newAudioFilesData.albums[i] = album2;
                                }
                            }
                        }
                        return newAudioFilesData;
                    default:
                        log('unsupported sub_event in audio_files,', e.body.msg.sub_event);
                        break;
                }
                break;
            case "RemoveAllCache":
                return {
                    folders: undefined,
                    foldersFetchStatus: AudioFilesFetchStatus.nothing,
                    albums: undefined,
                    albumsFetchStatus: AudioFilesFetchStatus.nothing,
                };
            case "FoldersFetching":
                newAudioFilesData = { ...currentAudioFilesData };
                if (newAudioFilesData.folders === undefined) {
                    newAudioFilesData.foldersFetchStatus = AudioFilesFetchStatus.fetching;
                }
                return newAudioFilesData;
            case "AlbumsFetching":
                newAudioFilesData = { ...currentAudioFilesData };
                if (newAudioFilesData.albums === undefined) {
                    newAudioFilesData.albumsFetchStatus = AudioFilesFetchStatus.fetching;
                }
                return newAudioFilesData;
            case "FolderDetailFetching":
                newAudioFilesData = { ...currentAudioFilesData };
                for (let i = 0; i < newAudioFilesData.folders.length; i++) {
                    const folder = newAudioFilesData.folders[i];
                    if (folder["path"] === e.body.path) {
                        if (folder["tracks"] === undefined) {
                            const folder2 = { ...folder }
                            folder2["tracksFetchStatus"] = AudioFilesFetchStatus.fetching;
                            newAudioFilesData.folders[i] = folder2;
                        }
                    }
                }
                return newAudioFilesData;
            case "AlbumDetailFetching":
                newAudioFilesData = { ...currentAudioFilesData };
                for (let i = 0; i < newAudioFilesData.albums.length; i++) {
                    const album = newAudioFilesData.albums[i];
                    if (album["name"] === e.body.name) {
                        if (album["tracks"] === undefined) {
                            const album2 = { ...album }
                            album2["tracksFetchStatus"] = AudioFilesFetchStatus.fetching;
                            newAudioFilesData.albums[i] = album2;
                        }
                    }
                }
                return newAudioFilesData;
            default:
                log('unsupported event in useAudioController reducer,', e.event);
                break;
        }
    }, []);

    const [audioFilesData, dispatchEvent] = useReducer(reducer, {
        folders: undefined,
        foldersFetchStatus: AudioFilesFetchStatus.nothing,
        albums: undefined,
        albumsFetchStatus: AudioFilesFetchStatus.nothing,
    });

    const removeAllCaches = useCallback(() => {
        dispatchEvent({ event: "RemoveAllCache", body: {} });
    }, []);

    const onAudioFilesEvent = useCallback((message) => {
        dispatchEvent({ event: "GatewayAudioFilesEvent", body: message });
    }, []);

    const fetchFolders = useCallback(() => {
        sendEvent("audio_files", { "sub_event": "folder_list" });
        dispatchEvent({ event: "FoldersFetching", body: {} });
    }, [sendEvent]);

    const fetchAlbums = useCallback(() => {
        sendEvent("audio_files", { "sub_event": "album_list" });
        dispatchEvent({ event: "AlbumsFetching", body: {} });
    }, [sendEvent]);

    const fetchFolderDetail = useCallback((path) => {
        sendEvent("audio_files", { "sub_event": "track_list", "list_type": 1, "list_path": path });
        dispatchEvent({ event: "FolderDetailFetching", body: { path } });
    }, [sendEvent]);

    const fetchAlbumDetail = useCallback((name) => {
        sendEvent("audio_files", { "sub_event": "track_list", "list_type": 2, "list_name": name });
        dispatchEvent({ event: "AlbumDetailFetching", body: { name } });
    }, [sendEvent]);

    return {
        onAudioFilesEvent,
        audioFilesData,
        audioFilesController: {
            removeAllCaches,
            fetchFolders, fetchAlbums, fetchFolderDetail, fetchAlbumDetail,
            onAudioFilesEvent
        }
    }
}
